import { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { getSessionToken } from '../utils/session';

// Thin wrapper around the socket.io room for one live presentation session.
// Used by PresenterView (emits slide:change), AudienceScreen and
// PresentationPlayer (both just listen). Authorization for the emitting side
// happens server-side (server.js only accepts events from the verified user
// that started the session) — this hook doesn't attempt to enforce that itself.
export default function useLiveSession(sessionId, userId) {
  const socketRef = useRef(null);
  const [slideIndex, setSlideIndex] = useState(0);
  const [connected, setConnected] = useState(false);
  const [ended, setEnded] = useState(false);
  const [questionVersion, setQuestionVersion] = useState(0);

  useEffect(() => {
    if (!sessionId) return undefined;
    // The token lets the server confirm who is presenting. Audience windows
    // have none and only listen.
    const socket = io({ transports: ['websocket', 'polling'], auth: { token: getSessionToken() } });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      socket.emit('session:join', { sessionId });
    });
    socket.on('disconnect', () => setConnected(false));
    socket.on('session:state', (state) => {
      if (typeof state?.slideIndex === 'number') setSlideIndex(state.slideIndex);
    });
    socket.on('slide:change', ({ index }) => setSlideIndex(index));
    socket.on('question:new', () => setQuestionVersion((version) => version + 1));
    socket.on('session:end', () => setEnded(true));

    return () => socket.disconnect();
  }, [sessionId]);

  const changeSlide = useCallback((index) => {
    socketRef.current?.emit('slide:change', { sessionId, userId, index });
  }, [sessionId, userId]);

  return { slideIndex, connected, ended, questionVersion, changeSlide };
}
